import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDragonStatus } from '../api/dragon';
import type { DragonStatus } from '../types/dragon';

// SF-122 Phase 3 Decision 4: module-level TTL cache, mirroring `useKingdomRelationships`'s exact
// existing pattern (same shape, same TTL, same test-isolation seam) rather than inventing a new
// caching strategy for this second kingdom-scoped status hook.
interface CacheEntry {
  data: DragonStatus;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000; // 60 seconds

export function _resetDragonStatusCache() {
  cache.clear();
}

interface UseDragonStatusResult {
  data: DragonStatus | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches dragon status for a kingdom, wrapping the existing
 * `GET /api/worlds/{worldId}/kingdoms/{kingdomId}/dragon` endpoint (the same data `Dragons.tsx`
 * already renders via its own hand-rolled fetch).
 *
 * Uses a module-level TTL cache (60s), keyed `${worldId}:${kingdomId}` — same shape as
 * `useKingdomRelationships`'s cache. Callers must pass the canonical
 * `province.metadata.location.kingdomId`, not a differently-derived key.
 */
export function useDragonStatus(kingdomId: string | null): UseDragonStatusResult {
  const { worldId } = useAuth();
  const [data, setData] = useState<DragonStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!kingdomId || !worldId) {
      setData(null);
      return;
    }

    const cacheKey = `${worldId}:${kingdomId}`;
    const now = Date.now();
    const cached = cache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      setData(cached.data);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getDragonStatus(worldId, kingdomId);
      cache.set(cacheKey, { data: result, expiresAt: now + CACHE_TTL_MS });
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dragon status');
    } finally {
      setIsLoading(false);
    }
  }, [kingdomId, worldId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { data, isLoading, error };
}
